/**
 * The wall plan: one wall seen face-on — the app's WallSide view, Lite's `wallView`. An
 * orthographic camera stands inside the room in front of the chosen face; only that wall,
 * its openings and what stands against it are drawn, over a metre grid, with dimension
 * rows around. It is a plane of the 2D plan (the top switch stays on 2D), reached from the
 * floor / ceiling / wall switch. This module holds the state rules and the frame maths that
 * the viewer, the overlay and the tests share.
 */
import type { Item, Level, Room, Vec2, Wall } from '../core/hp/types';
import { itemFootprint, type Footprints } from '../core/geometry/footprint';
import { verticalExtent } from '../core/geometry/stacking';
import { contains } from '../core/geometry/edit';

export type WallSide = 'front' | 'back';
/** Which wall, seen from which of its faces. `front` is the face `normalIn` points at. */
export interface WallViewTarget { wallId: string; side: WallSide }

/** Free space either side of the wall in the frame at zoom 1, in metres (Lite: L + 1.2). */
export const WALL_PLAN_MARGIN = 1.2;
/** 1× frames the wall with its margins; the frame zooms out to a quarter of that and in to eight times. */
export const WALL_PLAN_ZOOM = { min: .25, max: 8 } as const;
/** How far in front of the face an item's corner may stand and still belong to the wall (the app's IsShownIn2D). */
export const WALL_PLAN_REACH = .2;
/** The camera never stands further than this from the face, even in a hall. */
const CAMERA_REACH = 6;
/** A room probe beyond a face: half the thickness plus a few centimetres, as `wallFacts()` probes. */
const PROBE_BEYOND = .06;

const clamp = (value: number, lo: number, hi: number) => value < lo ? lo : value > hi ? hi : value;
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z;

export const otherSide = (side: WallSide): WallSide => side === 'front' ? 'back' : 'front';

/** Unit normal out of the given face. */
export function faceNormal(wall: Wall, side: WallSide): Vec2 {
  return side === 'front' ? { x: wall.normalIn.x, z: wall.normalIn.z } : { x: -wall.normalIn.x, z: -wall.normalIn.z };
}
/** Distance from the wall's start along its direction. */
export const alongWall = (wall: Wall, point: Vec2): number => dot({ x: point.x - wall.start.x, z: point.z - wall.start.z }, wall.dir);
/** Signed distance from the wall's centre line, positive towards the face `side`. */
export const acrossWall = (wall: Wall, side: WallSide, point: Vec2): number =>
  dot({ x: point.x - wall.start.x, z: point.z - wall.start.z }, faceNormal(wall, side));

/** The tightest room a few centimetres beyond the face, probed at three points along it. */
function roomBeyond(level: Pick<Level, 'rooms'>, wall: Wall, side: WallSide): Room | undefined {
  const n = faceNormal(wall, side), beyond = wall.thickness / 2 + PROBE_BEYOND;
  let best: Room | undefined;
  for (const u of [.5, .25, .75]) {
    const probe = { x: wall.start.x + wall.dir.x * wall.length * u + n.x * beyond, z: wall.start.z + wall.dir.z * wall.length * u + n.z * beyond };
    for (const room of level.rooms) if (room.polygon.length >= 3 && contains(room.polygon, probe) && (!best || room.area < best.area)) best = room;
    if (best) return best;
  }
  return best;
}

/** A face with a room behind it — the only faces the wall plan shows. */
export function isInteriorSide(level: Pick<Level, 'rooms'>, wall: Wall, side: WallSide): boolean {
  return !!roomBeyond(level, wall, side);
}

/** Every face the plan can show, in wall order and front before back — the order ‹ › step through (the app's NextWall2D). */
export function wallViewCandidates(level: Pick<Level, 'walls' | 'rooms'>): WallViewTarget[] {
  const result: WallViewTarget[] = [];
  for (const wall of level.walls) {
    if (wall.hidden) continue;
    for (const side of ['front', 'back'] as const) if (isInteriorSide(level, wall, side)) result.push({ wallId: wall.id, side });
  }
  return result;
}

/**
 * Where the wall plan opens (the app's ViewWallSide): the longest interior face of the selected
 * item's room — else of the largest room — preferring the faces whose other side is outdoors.
 */
export function defaultWallView(level: Pick<Level, 'walls' | 'rooms'>, selected?: Item | null): WallViewTarget | null {
  const room = (selected && level.rooms.find(candidate => candidate.polygon.length >= 3 && contains(candidate.polygon, selected.position)))
    || level.rooms.reduce<Room | undefined>((best, candidate) => !best || candidate.area > best.area ? candidate : best, undefined);
  const candidates: Array<{ target: WallViewTarget; exterior: boolean; length: number }> = [];
  for (const target of wallViewCandidates(level)) {
    const wall = level.walls.find(candidate => candidate.id === target.wallId)!;
    if (room && roomBeyond(level, wall, target.side)?.index !== room.index) continue;
    candidates.push({ target, exterior: !isInteriorSide(level, wall, otherSide(target.side)), length: wall.length });
  }
  const pool = candidates.some(candidate => candidate.exterior) ? candidates.filter(candidate => candidate.exterior) : candidates;
  return pool.reduce<typeof pool[number] | null>((best, candidate) => !best || candidate.length > best.length ? candidate : best, null)?.target ?? null;
}

/** The face `step` places on from `current`; null past the first or the last face (the app's NextWall2D wraps round). */
export function stepWallView(level: Pick<Level, 'walls' | 'rooms'>, current: WallViewTarget, step: number): WallViewTarget | null {
  const place = wallViewPlace(level, current);
  return place ? wallViewCandidates(level)[place.index + step] ?? null : null;
}

/** Where `current` stands among the faces, a missing face counting as the first; null when no face qualifies. */
export function wallViewPlace(level: Pick<Level, 'walls' | 'rooms'>, current: WallViewTarget): { index: number; count: number } | null {
  const candidates = wallViewCandidates(level);
  if (!candidates.length) return null;
  return { index: Math.max(0, candidates.findIndex(candidate => candidate.wallId === current.wallId && candidate.side === current.side)), count: candidates.length };
}

/** The wall's height in the plan: its own, capped by the level's (Lite: min(height(of:), wallHeight)). */
export function wallPlanHeight(level: Pick<Level, 'height'>, wall: Wall): number {
  return level.height > 0 ? Math.min(wall.height, level.height) : wall.height;
}

/** Wall-mounted by the app's rule: the wall plan moves these up and down, floor items only along the wall. */
export function wallPlanVertical(item: Item): boolean {
  return item.placement === 'wall' || (item.elevation >= .5 && item.size.d <= .25 && item.placement !== 'ceiling');
}

/**
 * What the wall plan shows besides the wall (the app's IsShownIn2D for WallSide): an item with
 * a corner in front of the face, within 20 cm of it and within 30 cm of the wall's ends.
 * Ceiling items never show.
 */
export function itemFacesWall(item: Item, wall: Wall, side: WallSide, footprints?: Footprints): boolean {
  if (item.placement === 'ceiling') return false;
  const half = wall.thickness / 2;
  for (const corner of itemFootprint(item, footprints)) {
    const depth = acrossWall(wall, side, corner) - half, along = alongWall(wall, corner);
    if (depth >= -.03 && depth <= WALL_PLAN_REACH && along >= -.3 && along <= wall.length + .3) return true;
  }
  return false;
}

export function wallPlanItems(level: Pick<Level, 'walls' | 'items'>, target: WallViewTarget, footprints?: Footprints): Item[] {
  const wall = level.walls.find(candidate => candidate.id === target.wallId);
  return wall ? level.items.filter(item => itemFacesWall(item, wall, target.side, footprints)) : [];
}

/**
 * How far in front of the face the camera stands: up to 6 m, but never beyond the first wall
 * across the room (less 25 cm), so the far walls and the street stay out of the picture.
 */
export function wallPlanCameraDistance(level: Pick<Level, 'walls'>, wall: Wall, side: WallSide): number {
  const n = faceNormal(wall, side), mid = wall.center;
  let free = CAMERA_REACH;
  for (const other of level.walls) {
    if (other.id === wall.id) continue;
    const a = { x: other.start.x - mid.x, z: other.start.z - mid.z }, b = { x: other.end.x - mid.x, z: other.end.z - mid.z };
    const da = a.x * n.z - a.z * n.x, db = b.x * n.z - b.z * n.x;
    if (da * db > 0 || Math.abs(da - db) < 1e-9) continue;
    const s = da / (da - db), t = dot({ x: a.x + (b.x - a.x) * s, z: a.z + (b.z - a.z) * s }, n);
    if (t > .3) free = Math.min(free, t - .25);
  }
  return free;
}

/** +1 when the wall runs left to right on screen (its start on the left), -1 when it reads mirrored. */
export function wallDirectionSign(wall: Wall, side: WallSide): 1 | -1 {
  const n = faceNormal(wall, side);
  return n.z * wall.dir.x - n.x * wall.dir.z >= 0 ? 1 : -1;
}

/** The mapping between the wall's face (metres along, metres up) and the canvas (CSS pixels). */
export interface WallPlanFrame {
  wall: Wall;
  side: WallSide;
  /** The wall's height in the plan, metres. */
  height: number;
  /** The canvas, CSS pixels. */
  width: number;
  viewHeight: number;
  zoom: number;
  /** Where the frame's centre sits relative to the wall's centre: screen right and up, metres. */
  pan: { x: number; y: number };
  /** +1 when the wall runs left to right on screen. */
  sign: 1 | -1;
  /**
   * Pixels kept free at either side of the canvas — the rails, the inspector, the chat column — so
   * the wall and its dimension rows sit in the part of the canvas the chrome leaves free.
   */
  inset?: WallPlanInsets;
}
export interface WallPlanInsets {
  left: number; right: number;
  /**
   * Pixels of the free part a floating panel covers on the right — the inspector, the catalog. It never
   * changes the scale: the wall keeps its size when a panel opens, and may slide left by this much so
   * what the panel hides can be brought into view.
   */
  cover?: number;
}

/** The rails' claim on a canvas with no other chrome known: 8% a side, 48 px at least, 112 px at most. */
export const wallPlanInset = (width: number): number => Math.min(112, Math.max(48, width * .08));
const insetsOf = (frame: { width: number; inset?: WallPlanInsets }): WallPlanInsets => frame.inset ?? { left: 0, right: 0 };
/** Where the free part of the canvas has its centre — the wall's centre at pan 0. */
export const wallPlanCentreX = (frame: { width: number; inset?: WallPlanInsets }): number => {
  const { left, right } = insetsOf(frame);
  return left + (frame.width - left - right) / 2;
};

export const pxPerMetre = (frame: Pick<WallPlanFrame, 'wall' | 'width' | 'zoom' | 'inset'>): number => {
  const { left, right } = insetsOf(frame);
  return Math.max(frame.width - left - right, 1) * frame.zoom / (frame.wall.length + WALL_PLAN_MARGIN);
};

export function wallPlanToScreen(frame: WallPlanFrame, t: number, h: number): { x: number; y: number } {
  const ppm = pxPerMetre(frame);
  return { x: wallPlanCentreX(frame) + (frame.sign * (t - frame.wall.length / 2) - frame.pan.x) * ppm,
    y: frame.viewHeight / 2 - (h - frame.height / 2 - frame.pan.y) * ppm };
}

export function wallPlanFromScreen(frame: WallPlanFrame, x: number, y: number): { t: number; h: number } {
  const ppm = pxPerMetre(frame);
  return { t: frame.wall.length / 2 + frame.sign * ((x - wallPlanCentreX(frame)) / ppm + frame.pan.x),
    h: frame.height / 2 + frame.pan.y - (y - frame.viewHeight / 2) / ppm };
}

/** The wall never leaves the frame: the centre may wander only as far as the margins allow at this zoom. */
export function clampWallPan(pan: { x: number; y: number }, zoom: number, length: number, height: number, width: number, viewHeight: number, inset: WallPlanInsets = { left: 0, right: 0 }): { x: number; y: number } {
  // The limits read the free part of the canvas: past 1× the frame may slide as far as the wall's
  // margins allow there, so a zoom about a point keeps that point even next to a rail.
  const effective = Math.max(width - inset.left - inset.right, 1), ppm = effective * zoom / (length + WALL_PLAN_MARGIN);
  const visibleWidth = effective / ppm, visibleHeight = viewHeight / ppm;
  const limitX = Math.max(0, (length + WALL_PLAN_MARGIN) / 2 - visibleWidth / 2);
  const limitY = Math.max(0, (height + WALL_PLAN_MARGIN) / 2 - visibleHeight / 2), reach = Math.max(0, inset.cover ?? 0) / ppm;
  // `+ 0` folds a -0 from an empty range into 0, so a reset frame compares equal to a fresh one.
  return { x: clamp(pan.x, -limitX, limitX + reach) + 0, y: clamp(pan.y, -limitY, limitY) + 0 };
}

/** Zoom by `factor` about a canvas point: what was under the pointer stays under it, as on the plan. */
export function zoomWallPlanAt(frame: WallPlanFrame, factor: number, x: number, y: number): { zoom: number; pan: { x: number; y: number } } {
  const zoom = clamp(frame.zoom * factor, WALL_PLAN_ZOOM.min, WALL_PLAN_ZOOM.max);
  const before = pxPerMetre(frame), after = pxPerMetre({ ...frame, zoom });
  const dx = x - wallPlanCentreX(frame), dy = y - frame.viewHeight / 2;
  const anchor = { x: frame.pan.x + dx / before, y: frame.pan.y - dy / before };
  return { zoom, pan: clampWallPan({ x: anchor.x - dx / after, y: anchor.y + dy / after }, zoom, frame.wall.length, frame.height, frame.width, frame.viewHeight, insetsOf(frame)) };
}

/** Drag the frame with the pointer: the picture follows the finger, clamped like the zoom. */
export function panWallPlanBy(frame: WallPlanFrame, dxPx: number, dyPx: number): { x: number; y: number } {
  const ppm = pxPerMetre(frame);
  return clampWallPan({ x: frame.pan.x - dxPx / ppm, y: frame.pan.y + dyPx / ppm }, frame.zoom, frame.wall.length, frame.height, frame.width, frame.viewHeight, insetsOf(frame));
}

/** A rectangle on the face: metres along the wall and metres up. */
export interface WallPlanSpan { t0: number; t1: number; h0: number; h1: number }

/**
 * What the dimension rows measure (the app's LengthMarkers on a WallSide): openings and wall-mounted
 * items above the wall, floor items below; each clipped to the wall's extent.
 */
export function wallPlanSpans(level: Pick<Level, 'walls' | 'openings' | 'items'>, target: WallViewTarget, footprints?: Footprints): { upper: WallPlanSpan[]; lower: WallPlanSpan[] } {
  const wall = level.walls.find(candidate => candidate.id === target.wallId);
  if (!wall) return { upper: [], lower: [] };
  const upper: WallPlanSpan[] = level.openings.filter(opening => opening.wallId === wall.id)
    .map(opening => ({ t0: opening.offset - opening.width / 2, t1: opening.offset + opening.width / 2, h0: opening.sill, h1: opening.sill + opening.height }));
  const lower: WallPlanSpan[] = [];
  for (const item of level.items) {
    if (!itemFacesWall(item, wall, target.side, footprints)) continue;
    const ts = itemFootprint(item, footprints).map(corner => alongWall(wall, corner));
    const { bottom, top } = verticalExtent(item);
    const span = { t0: Math.max(Math.min(...ts), 0), t1: Math.min(Math.max(...ts), wall.length), h0: bottom, h1: top };
    if (span.t1 <= span.t0 + .01) continue;
    (wallPlanVertical(item) ? upper : lower).push(span);
  }
  return { upper, lower };
}

/** Every span edge plus the two ends, sorted and de-duplicated: a row labels each gap between neighbours. */
export function dimensionBounds(spans: ReadonlyArray<readonly [number, number]>, limit: number): number[] {
  const values = [0, limit];
  for (const [a, b] of spans) values.push(clamp(a, 0, limit), clamp(b, 0, limit));
  values.sort((a, b) => a - b);
  const unique: number[] = [];
  for (const value of values) if (!unique.length || value - unique[unique.length - 1] > .005) unique.push(value);
  return unique;
}
